import { useCallback, useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { LocationFeatureFlagSettingName } from '@weave/schema-gen-ts/dist/schemas/payments/messages/location_settings/location_settings.pb';
import { sendInvoiceReceipt } from '@frontend/api-invoices';
import { SetupFutureUsage } from '@frontend/api-weave-pay';
import { getUser } from '@frontend/auth-helpers';
import { HttpError } from '@frontend/fetch';
import { useTranslation } from '@frontend/i18n';
import { paymentsSentry, useLocationFeatureSetting } from '@frontend/payments-hooks';
import { StoredReader, TerminalAppData, TerminalReader } from '@frontend/payments-terminal-controller';
import { shell } from '@frontend/shell-utils';
import { theme } from '@frontend/theme';
import {
  Button,
  ConfirmationModal,
  ContentLoader,
  FieldChangeEvent,
  useAlert,
  useFormField,
} from '@frontend/design-system';
import { useInvoicePerson } from '../../../../hooks/use-invoice-person';
import { ReaderConnectErrors, useTerminalPaymentSession } from '../../../../hooks/use-terminal-payment-session';
import { useCollectPaymentMultiContext } from '../../../collect-payment-multi.context';
import { useHandleTerminalPaymentMethod } from '../../../hooks/use-handle-terminal-payment-method';
import { useScopedTrackingIds } from '../../../hooks/use-scoped-tracking-ids';
import { useUpdateRequestStatusPoll } from '../../../hooks/use-update-request-status-poll';
import { PAYMENT_MULTI_STEP_LABELS, PAYMENT_MULTI_STEPS } from '../../../utils/steps';
import { StyledStep } from '../../atoms/styled-step';
import { useCreateTerminalPaymentIntent } from '../../terminals/use-create-terminal-payment-intent';
import { UseTerminalReadersQuery } from '../../terminals/use-terminal-readers-query';
import { useTerminalSelectionData } from '../../terminals/use-terminal-selection-data';
import { CollectReaderPaymentDisplay } from './collect-terminal-step/collect-reader-payment-display';
import { CollectReaderPaymentDisplayResponsive } from './collect-terminal-step/collect-reader-payment-display-responsive';
import { ReaderStatusAdapter } from './collect-terminal-step/reader-status-adapter';
import { ReceiptSection } from './collect-terminal-step/receipt-section';
import { CollectTerminalStepBodyProps } from './collect-terminal.types';

const styles = {
  buttonBar: css`
    display: flex;
    justify-content: end;
    gap: ${theme.spacing(2)};
  `,
  receiptButtonBar: css`
    display: flex;
    justify-content: flex-end;
  `,
};

export type CollectTerminalStepProps = CollectTerminalStepBodyProps & {
  readers: TerminalReader[];
  selectedReader: StoredReader | undefined;
  isReadersLoading?: boolean;
  tempReader: TerminalReader | null;
  refetchReaders: UseTerminalReadersQuery['refetchReadersAsync'];
  setSelectedReader: (reader: StoredReader) => void;
  setTempReader: React.Dispatch<React.SetStateAction<TerminalReader | null>>;
};

export const CollectTerminalStep = ({
  readers,
  tempReader,
  selectedReader,
  isReadersLoading,
  onGoBack,
  refetchReaders,
  onPaymentSuccess,
  setTempReader,
  setSelectedReader,
  onFinishAndClose,
}: CollectTerminalStepProps) => {
  const { t } = useTranslation('payments');
  const [sendingReceipt, setSendingReceipt] = useState(false);
  const user = getUser();

  const {
    activeLocationId,
    invoice,
    activeGateway,
    invoiceQuery: { refetch: refetchInvoice },
    isResponsiveMode,
    multiStep,
  } = useCollectPaymentMultiContext();

  const locationId = invoice?.locationId || activeLocationId;
  const [hasConfirmedSuccess, setHasConfirmedSuccess] = useState(false);
  const [hasPollEnded, setHasPollEnded] = useState(false);
  const [isRetryingIntent, setIsRetryingIntent] = useState(false);

  const alert = useAlert();

  // PAYMENT INTENT
  const { person } = useInvoicePerson(invoice);

  /**
   * Should only use "offSession" when we have a "save for later"
   * checkbox and corresponding address fields, etc.
   */
  const setupFutureUsage = SetupFutureUsage.offSession;

  const onRefetchInvoice = useCallback(() => {
    return refetchInvoice().then((res) => res.data);
  }, [refetchInvoice]);

  const {
    paymentIntent,
    paymentUuid,
    error: createIntentError,
    retryCreatePaymentIntent,
  } = useCreateTerminalPaymentIntent({
    person,
    invoice,
    setupFutureUsage,
    onGoBack,
    onRefetchInvoice,
  });

  const {
    isDeletingPaymentMethod,
    deletePaymentMethod,
    isAttachingPaymentMethod,
    attachPaymentMethod,
    isLoadingGetStripePaymentMethod,
  } = useHandleTerminalPaymentMethod({ locationId, invoice, paymentUuid });

  const { enabled: autoReceiptEnabled } = useLocationFeatureSetting(
    LocationFeatureFlagSettingName.AUTO_RECEIPT_ENABLED,
    locationId || ''
  );

  const appData: TerminalAppData = useMemo(
    () =>
      shell.isShell
        ? {
            type: 'shell',
            version: shell.version,
          }
        : {
            type: 'nwx',
            version: undefined,
          },
    [shell]
  );

  const { setProcessingAndStartPoll } = useUpdateRequestStatusPoll(
    invoice,
    () => {
      onPaymentSuccess();
      setHasPollEnded(true);
      setHasConfirmedSuccess(true);
    },
    () => {
      setHasConfirmedSuccess(false);
      setHasPollEnded(true);
    }
  );

  const {
    error: readerError,
    status: readerStatus,
    isLoading: isTerminalStateLoading,
    isTerminalStateDisabled,
    handleCancel,
    handleTryAgain,
    isPaymentSuccessful,
  } = useTerminalPaymentSession({
    reader: selectedReader,
    locationId: locationId ?? '',
    appData,
    paymentIntent: paymentIntent?.paymentIntent,
    paymentId: paymentIntent?.paymentId,
    invoiceId: invoice?.id,
    goBack: onGoBack,
    retryCreatePaymentIntent,
    discoverReaders: refetchReaders,
    onPaymentSuccess: () => {
      setProcessingAndStartPoll();
    },
  });

  const {
    modalProps,
    handleReaderSelect,
    onConfirm: onClickConfirmPayment,
  } = useTerminalSelectionData({
    readers,
    tempReader,
    selectedReader,
    locationId,
    onSelectReader: async (selected) => {
      setSelectedReader(selected);
    },
    onSelectTempReader: (reader) => {
      setTempReader(reader);
    },
  });

  const handleEndActivePayment = () => {
    handleTryAgain({ selectedReader, failIfReaderInUse: false });
  };

  const error = createIntentError && !isRetryingIntent ? createIntentError : readerError;

  const isTerminalInUse = !!(error?.message === ReaderConnectErrors.alreadyInUse);

  const { personEmail } = useInvoicePerson(invoice);

  const emailReceiptFieldProps = useFormField({
    type: 'list',
    maxAllowed: 3,
  });

  const saveCardCheckboxFieldProps = useFormField({
    type: 'checkbox',
    value: false,
  });

  const saveCardZipFieldProps = useFormField({
    type: 'postalCode',
    value: person?.address?.postalCode || '',

    required: saveCardCheckboxFieldProps.value,
    validateOnChange: true,
  });

  const handleSendReceipt = async () => {
    setSendingReceipt(true);
    try {
      await sendInvoiceReceipt({
        invoiceId: invoice?.id as string,
        emails: emailReceiptFieldProps.value,
        userId: user?.userID,
      });
      alert.success(t('Receipt sent successfully'));
    } catch (err) {
      console.error('Error sending receipt', err);

      if (err instanceof HttpError && err.status !== 200) {
        alert.error(err.message);
        paymentsSentry.error('Failed to send invoice receipt', 'TerminalReceipt', err.message);
      } else if (err instanceof Error) {
        alert.error(t('Failed to send receipt'));
        paymentsSentry.error('Failed to send invoice receipt', 'TerminalReceipt', err.message);
      } else {
        paymentsSentry.error('Failed to send invoice receipt', 'TerminalReceipt', 'Unknown error');
      }
    }
    setSendingReceipt(false);
  };

  const attachOrDetachPaymentMethod = async () => {
    try {
      if (saveCardCheckboxFieldProps.value) {
        /**
         * Save card on file
         */
        if (!paymentIntent?.paymentIntent?.id) {
          throw new Error('Payment intent ID is required to attach payment method');
        }

        await attachPaymentMethod({
          locationId: locationId,
          paymentIntentId: paymentIntent?.paymentIntent?.id,
          postalCode: saveCardZipFieldProps.value?.trim() || '',
          personId: person?.personId,
        });
        alert.success(t('Payment method saved successfully'));
      } else {
        /**
         * remove card on file
         */
        await deletePaymentMethod({
          locationId: locationId,
        });
      }
    } catch (err) {
      console.error('Error saving payment method', err);
    }
  };

  const onClickFinish = async () => {
    if (saveCardCheckboxFieldProps.value && (!saveCardZipFieldProps.value?.trim() || saveCardZipFieldProps.error)) {
      /**
       * First make sure we don't need to uncheck save card or fill in email before continuing,
       * so we don't send the receipt multiple times.
       */

      const fieldChangeEvent = {
        name: 'zip',
        value: saveCardZipFieldProps.value?.trim() || '',
      } as FieldChangeEvent<any>;

      saveCardZipFieldProps.onChange(fieldChangeEvent);

      return;
    }

    await attachOrDetachPaymentMethod();

    if (emailReceiptFieldProps.value.length) {
      await handleSendReceipt();
    }

    onFinishAndClose();
  };

  const handleRetryingIntent = useCallback(async () => {
    setIsRetryingIntent(true);
    try {
      await retryCreatePaymentIntent();
    } finally {
      setIsRetryingIntent(false);
    }
  }, [retryCreatePaymentIntent]);

  const isFinalizing = sendingReceipt || isDeletingPaymentMethod || isAttachingPaymentMethod;

  const isFinishDisabled = isFinalizing || isLoadingGetStripePaymentMethod;

  const {
    changePaymentTrackingId,
    changePaymentTrackingIntentId,
    tryAgainTrackingId,
    tryAgainIntentTrackingId,
    cancelTrackingId,
    sendReceiptTrackingId,
    refreshReadersBtnTrackingId,
  } = useScopedTrackingIds({
    changePaymentTrackingId: `${PAYMENT_MULTI_STEPS.collectTerminal}--${activeGateway}--change-payment-btn`,
    changePaymentTrackingIntentId: `${PAYMENT_MULTI_STEPS.collectTerminal}--${activeGateway}--change-payment-intent-btn`,
    tryAgainTrackingId: `${PAYMENT_MULTI_STEPS.collectTerminal}--${activeGateway}--try-again-btn`,
    tryAgainIntentTrackingId: `${PAYMENT_MULTI_STEPS.collectTerminal}--${activeGateway}--try-again-intent-btn`,
    cancelTrackingId: `${PAYMENT_MULTI_STEPS.collectTerminal}--${activeGateway}--cancel-btn`,
    sendReceiptTrackingId: `${PAYMENT_MULTI_STEPS.collectTerminal}--${activeGateway}--send-receipt-btn`,
    refreshReadersBtnTrackingId: `${PAYMENT_MULTI_STEPS.collectTerminal}--${activeGateway}--refresh-readers-btn`,
  });

  const showReceiptSection = !isTerminalInUse && isPaymentSuccessful && (hasConfirmedSuccess || hasPollEnded);

  useEffect(() => {
    multiStep.setShowCancelAction(!showReceiptSection);
  }, [showReceiptSection]);

  return (
    <StyledStep
      id={PAYMENT_MULTI_STEPS.collectTerminal}
      label={PAYMENT_MULTI_STEP_LABELS[PAYMENT_MULTI_STEPS.collectTerminal]}
      components={{
        Footer: () => (
          <div className='step__footer'>
            {!isTerminalInUse &&
              (!isPaymentSuccessful ? (
                <div css={styles.buttonBar}>
                  {readerError ? (
                    <>
                      <Button trackingId={changePaymentTrackingId} variant='secondary' size='large' onClick={onGoBack}>
                        {t('Change Payment Method')}
                      </Button>
                      <Button trackingId={tryAgainTrackingId} size='large' onClick={() => handleTryAgain()}>
                        {t('Try Again')}
                      </Button>
                    </>
                  ) : createIntentError ? (
                    <>
                      <Button
                        trackingId={changePaymentTrackingIntentId}
                        variant='secondary'
                        size='large'
                        onClick={onGoBack}
                      >
                        {t('Change Payment Method')}
                      </Button>
                      <Button
                        trackingId={tryAgainIntentTrackingId}
                        size='large'
                        onClick={handleRetryingIntent}
                        disabled={isRetryingIntent}
                      >
                        {t('Try Again')}
                      </Button>
                    </>
                  ) : (
                    <Button trackingId={cancelTrackingId} variant='secondary' size='large' onClick={handleCancel}>
                      {t('Cancel')}
                    </Button>
                  )}
                </div>
              ) : (
                <>
                  {hasPollEnded && (
                    <div css={styles.receiptButtonBar}>
                      <Button
                        trackingId={sendReceiptTrackingId}
                        size='large'
                        disabled={isFinishDisabled}
                        loading={isFinalizing}
                        onClick={onClickFinish}
                      >
                        {t('Finish')}
                      </Button>
                    </div>
                  )}
                </>
              ))}
          </div>
        ),
      }}
    >
      <ContentLoader show={sendingReceipt} message={t('Sending receipt...')} />

      {isResponsiveMode && (
        <CollectReaderPaymentDisplayResponsive
          selectedReader={selectedReader}
          isReadersLoading={isReadersLoading || isTerminalStateLoading}
          refreshReadersBtnTrackingId={refreshReadersBtnTrackingId}
          ReaderStatusOverrideComponent={
            <ReaderStatusAdapter
              invoice={invoice}
              status={readerStatus}
              hasConfirmedSuccess={hasConfirmedSuccess}
              hasPollEnded={hasPollEnded}
              error={error}
              isTerminalInUse={isTerminalInUse}
              handleEndActivePayment={handleEndActivePayment}
            />
          }
          availableReaders={readers}
          onSelectedReaderChange={handleReaderSelect}
          onRefetchReaders={refetchReaders}
          disableTerminalSelection={isTerminalStateDisabled}
        />
      )}

      {!isResponsiveMode && (
        <CollectReaderPaymentDisplay
          selectedReader={selectedReader}
          ReaderStatusOverrideComponent={
            <ReaderStatusAdapter
              invoice={invoice}
              status={readerStatus}
              hasConfirmedSuccess={hasConfirmedSuccess}
              hasPollEnded={hasPollEnded}
              error={error}
              isTerminalInUse={isTerminalInUse}
              handleEndActivePayment={handleEndActivePayment}
            />
          }
          availableReaders={readers}
          onSelectedReaderChange={handleReaderSelect}
          disableTerminalSelection={isTerminalStateDisabled}
        />
      )}

      {showReceiptSection && (
        <ReceiptSection
          emailFieldProps={emailReceiptFieldProps}
          saveCardZipFieldProps={saveCardZipFieldProps}
          saveCardCheckboxFieldProps={saveCardCheckboxFieldProps}
          invoice={invoice}
          personEmail={personEmail}
          autoReceiptEnabled={autoReceiptEnabled}
        />
      )}

      <ConfirmationModal
        {...modalProps}
        title={t('This terminal is in use')}
        message={t('Override terminal payment, or select another terminal.')}
        onConfirm={onClickConfirmPayment}
        confirmLabel={t('Override Terminal Payment')}
        onCancel={modalProps.onClose}
      />
    </StyledStep>
  );
};
